// src/admin/pages/Announcements.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid, Card, CardContent, Typography, TextField, FormControl, InputLabel,
  Select, MenuItem, Button, IconButton, Snackbar, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { fetchUsers, fetchAnnouncements, sendAnnouncement, deleteAnnouncement } from '../lib/adminApi';
import { toDate } from '../lib/adminLogic';

export default function Announcements() {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipient, setRecipient] = useState('all');
  const [toDelete, setToDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const reload = useCallback(async () => {
    setUsers(await fetchUsers());
    setMessages(await fetchAnnouncements());
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const onSend = async () => {
    if (!subject.trim() || !body.trim()) return setToast({ severity: 'error', msg: 'Oggetto e testo obbligatori' });
    await sendAnnouncement(currentUser, { subject, body, recipientIds: recipient === 'all' ? [] : [recipient] });
    setSubject(''); setBody(''); setRecipient('all');
    setToast({ severity: 'success', msg: 'Annuncio inviato' });
    await reload();
  };

  const onDelete = async () => {
    await deleteAnnouncement(currentUser, toDelete);
    setToDelete(null);
    setToast({ severity: 'success', msg: 'Annuncio eliminato' });
    await reload();
  };

  const columns = [
    { key: 'subject', label: 'Oggetto', sortable: true },
    { key: 'audience', label: 'Destinatari', render: (m) => (m.recipientIds?.length ? `${m.recipientIds.length} utenti` : 'Tutti') },
    { key: 'read', label: 'Letture', render: (m) => (m.readBy?.length || 0) },
    { key: 'timestamp', label: 'Data', render: (m) => toDate(m.timestamp)?.toLocaleString() || '' },
  ];

  return (
    <>
      <PageHeader title="Annunci" subtitle="Invia e gestisci i messaggi agli utenti" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card><CardContent>
            <Typography variant="h6" gutterBottom>Nuovo annuncio</Typography>
            <TextField fullWidth label="Oggetto" margin="normal" value={subject} onChange={(e) => setSubject(e.target.value)} />
            <TextField fullWidth label="Testo" margin="normal" multiline rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
            <FormControl fullWidth margin="normal">
              <InputLabel>Destinatario</InputLabel>
              <Select value={recipient} label="Destinatario" onChange={(e) => setRecipient(e.target.value)}>
                <MenuItem value="all">Tutti gli utenti</MenuItem>
                {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.email}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="contained" sx={{ mt: 2 }} onClick={onSend}>Invia</Button>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <DataTable
            columns={columns} rows={messages} searchable searchKeys={['subject']}
            rowActions={(m) => <IconButton color="error" onClick={() => setToDelete(m)} aria-label="Elimina"><DeleteIcon /></IconButton>}
          />
        </Grid>
      </Grid>

      <ConfirmDialog
        open={!!toDelete} title="Elimina annuncio" confirmLabel="Elimina"
        message={`Eliminare "${toDelete?.subject}"?`} onConfirm={onDelete} onClose={() => setToDelete(null)}
      />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </>
  );
}
